/**
 * Centralized `localStorage` / `sessionStorage` keys.
 *
 * Why this file exists:
 * - Every key the app reads or writes from browser storage should live here
 *   as a typed constant. String literals scattered across components cause
 *   silent typos and make it impossible to invalidate caches at logout
 *   or bump a key version when the shape of the stored value changes.
 *
 * Conventions:
 * - `lx:*` / `leadx:*` — first-party app settings owned by the player or shell.
 * - `pwa-*` / `pwa:*` — PWA install/update related state.
 * - `cc_*` / `survey_*` — lead-gen / confidence-check linking tokens.
 *
 * When you bump the shape of a stored value (e.g. JSON layout change),
 * append a `:v2` suffix to the key here. Old keys will be ignored on
 * reload and harmlessly garbage-collect themselves.
 *
 * Per-entity dynamic keys (per user, per course, per unit, per test) are
 * exposed as functions under `STORAGE_KEY_FACTORIES` so the prefix can
 * still be grep'd from one place.
 */
export const STORAGE_KEYS = {
  // Course player
  narrationEnabled: "lx:narrationEnabled",
  audioLanguage: "lx:audioLanguage",
  subtitlesEnabled: "lx:subtitlesEnabled",
  subtitleLanguage: "lx:subtitleLanguage",

  // PWA install / update prompts
  pwaIosInstallDismissed: "pwa-ios-install-dismissed",
  pwaAlreadyInstalledDismissed: "pwa-already-installed-dismissed",
  pwaInstallDismissed: "pwa-install-dismissed",
  pwaInstalledTimestamp: "pwa-installed-timestamp",
  pwaFabDismissed: "pwa-fab-dismissed-v1",

  // Lead linking (anonymous → authed)
  confidenceCheckPendingLeadId: "cc_pending_lead_id",
  surveyPendingLeadId: "survey_pending_lead_id",

  // Shell / admin
  viewAsUser: "leadx:viewAsUser:v1",
  
  referBannerDismissed: "refer_banner_dismissed_v1",
  adminLanguageMgmtSelectedCourse: "language-management-selected-course",
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * Per-entity dynamic keys. Keep the prefix here so anyone hunting for
 * "what does the app store under `bigDream:*`" can grep this file alone.
 */
export const STORAGE_KEY_FACTORIES = {
  addChildPromptDismissed: (userId: string) => `addChildPrompt:dismissed:${userId}`,
  bigDreamUnit: (courseId: string, unitId: string) => `bigDream:${courseId}:${unitId}`,
  parentOptionsHintDismissed: (userId: string, courseId: string) =>
    `parent_options_hint_dismissed:${userId}:${courseId}`,
  testProgress: (testId: string, userId: string) => `test_progress_${testId}_${userId}`,
  testPendingSubmit: (testId: string) => `test-submit-pending-${testId}`,
  /** One key per trial-engagement milestone so ad events fire exactly once. */
  trialCourseMilestone: (userId: string, courseId: string, slug: string) =>
    `lx:trialEvent:${userId}:${courseId}:${slug}`,
} as const;

